import * as React from 'react';

import { ProductCategoryContext } from '../productCategory/ProductCategoryProvider';
import FormatListBulletedRoundedIcon from '@material-ui/icons/FormatListBulletedRounded';
import DisplayModal from '../common/DisplayModal'; 
import CategoryModal from '../productCategory/CategoryModal';
import PropTypes from 'prop-types';

import styles from './ProductFilterNav.module.css';

/**
 *  This component is a "priority nav" where items that don't fit on
 *  a single line are added to the "more" dropdown.
 * 
 *  I'm buiding the "More" dropdown by counting the nav items that have wrapped to a new line and 
 *  enabling their display in the more dropdown.
 * 
 *  The only cheat AFAIK is that I'm reserving space for the "more" item
 *  while checking the wrapped items so there is a chance the last item might fit
 *  if I wasn't reserving space 
 */
const ProductFilterNav = ({currentCategory, handleClick}) => {

  const { productCategories } = React.useContext(ProductCategoryContext);

  // The filter bar
  const navRef = React.useRef();
  // Store a ref so we can debounce the resize handler
  const resizeTimout = React.useRef();
  // Number of items that ar ecurrently visible in the nav
  const visibleCount = React.useRef(0); 
  
  const [showMore, setShowMore] = React.useState(false);
  const handleMoreClick = () => {
    setShowMore(!showMore);
  };
  // toggle the More menu display
  const toggledStyle = showMore ? styles.moreClicked : '';

  const [showModal, setShowModal] = React.useState();

  // Add items that don't fit in filter bar to the "more" dropdown
  const checkFilterBar = React.useCallback(() => { 
    const overflowDetectionPixels = 20;
    // find overflow/non-visible nav items based on offsetTop !== ~0
    const navElem = navRef.current;
    if (navElem) {
      const navItems = navElem.querySelectorAll(`.${styles.filterNavItem}`);
      
      let visibleItemCount = 0;
      navItems.forEach((item, idx) => {
        if ( item.offsetParent && item.offsetTop <= overflowDetectionPixels ) {
          visibleItemCount++;
        } 
      });

      // REACT RENDERING QUIRK: Anytime the visible item count changes
      // you should rerun checkFilterBar to ensure an accurate count for the More dropdown
      if (visibleCount.current !== visibleItemCount) {
        resizeTimout.current = setTimeout(checkFilterBar, 10);
      }
      visibleCount.current = visibleItemCount;

      // Set attribute used by CSS to reserve spacve for the "More" link 
      navElem.setAttribute('data-show-more', visibleItemCount < navItems.length);
    }    
  },[visibleCount]); 

  React.useLayoutEffect(() => {
    // set initial filter nav content
    checkFilterBar();
    // adjust on window resize
    const handleResize = () => {
      window.clearTimeout(resizeTimout.current);
      resizeTimout.current = window.setTimeout(checkFilterBar, 100);
    };
    window.addEventListener('resize', handleResize);
   
    return () => { window.removeEventListener('resize', handleResize); };
  }, [productCategories, checkFilterBar]);

  return (
    <div ref={navRef} className={`${styles.filterNavWrap} ${currentCategory}`} data-show-more={showMore.current}>
      <ul className={styles.filterNav}>
        <li className={styles.filterMenu}
            onClick={() => setShowModal(true)}>
          <FormatListBulletedRoundedIcon />
        </li>
        { productCategories.map((category) => {
            const selectedStyle = currentCategory === category.display_name ? styles.selectedCategory : '';
            return (
              <li key={`link${category.name}`} 
                  onClick={()=> { handleClick(category.display_name) }} 
                  className={`${styles.filterNavItem} ${selectedStyle}`} 
                  data-val={category.name}>
                    {category.display_name}
              </li>
            )
        }) }
      </ul>  
      
      <div className={`${styles.moreTypes} ${toggledStyle}`} onClick={handleMoreClick}>
        More<span className={styles.moreIcon}>&or;</span>
        {/* duplicate list for the More drop-down: hide unneeded items */}
        <ul className={styles.moreTypesItems}>
          { productCategories.map((category, idx) => {
            // is category selected? 
            const selectedStyle = currentCategory === category.display_name ? styles.selectedCategory : '';
            // should it be displayed under More? (+2 due to submenu as first item)
            const visibleStyle = (idx + 2) > visibleCount.current ? styles.showMoreItem : ''; 
            return (
              <li key={`more${category.name}`} 
                  onClick={()=> { handleClick(category.display_name) }} 
                  className={`${styles.moreTypesItem} ${selectedStyle} ${visibleStyle}`} 
                  data-val={category.name}>
                    {category.display_name}
              </li>
            )
          }) }
        </ul>
      </div>
      { showModal &&
        <DisplayModal title="Product Categories" isShrinkToFit closeFn={() => setShowModal(false)}>
          <CategoryModal 
            currentCategory={currentCategory} 
            productCategories={productCategories} 
            handleClick={(categoryName) => { 
              handleClick(categoryName);
              setShowModal(false);
            }} /> 
        </DisplayModal>
      }
    </div>  
  )
}

ProductFilterNav.propTypes = {
  currentCategory: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired
}

export default ProductFilterNav;
